import * as React from 'react'
import * as PropTypes from 'prop-types'

import { setGACustomEvent } from '../../../services/analytics/google-analytics'
import Icon from '../../ui/Icon/Icon'
import truncateString from '../../../services/data/truncateString'
import getDeviceLocation from '../../../services/location/getDeviceLocation'
import { warmVideoCache } from '../../../services/media/cacheVideoUrl'

import {
  BannerRewardsImage,
  BannerTopGradient,
  BannerBottomGradient,
  BannerParticles,
  ContentContainer,
  ContentWrapper,
  ContentArea,
  SubtitleContainer,
  Subtitle,
  SelectMallWrapper,
  SelectMallButton,
  TitleContainer,
  Title,
  DropdownError,
  StyledLink,
  FeaturedSectionWrapper,
  BannerImageWrapper,
  BannerImage,
  BannerVideo,
  BannerWrapper,
  ViewButtonsContainer,
  ViewMallMap,
  ViewMallDirectory,
  PromosBannerOverlayTop,
  PromosBannerOverlayRight,
} from './HeroBanner.style'

const VIDEO_URL_PATTERN = /\.(mp4|webm|ogg)(?:[?#].*)?$/i

class HeroBanner extends React.Component {
  state = {
    lazyUrl: null,
  }

  imageLoader = null
  videoRef = React.createRef()
  videoWrapperRef = React.createRef()
  intersectionObserver = null
  playRetryTimers = []

  componentDidMount() {
    this.imageLoader = new Image()

    this.setState({
      lazyUrl: null,
    })

    if (!VIDEO_URL_PATTERN.test(this.props.desktopBackgroundUrl || '')) {
      this.lazyLoadImage()
    } else {
      this.initVideo(this.props.desktopBackgroundUrl)
    }
    getDeviceLocation()
      .then(this.getPosition)
      .catch(() => {})
  }

  componentDidUpdate(prevProps) {
    const { desktopBackgroundUrl } = this.props
    const useVideo = VIDEO_URL_PATTERN.test(desktopBackgroundUrl || '')

    if (useVideo) {
      if (
        prevProps.desktopBackgroundUrl !== desktopBackgroundUrl ||
        (!prevProps.desktopBackgroundUrl && desktopBackgroundUrl)
      ) {
        this.initVideo(desktopBackgroundUrl)
      }
      return
    }
    if (prevProps.desktopBackgroundUrl !== desktopBackgroundUrl) {
      this.lazyLoadImage()
    }
  }

  componentWillUnmount() {
    this.clearPlayRetries()
    this.imageLoader.removeEventListener('load', this.setLazyUrl)
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
    this.setState({
      lazyUrl: null,
    })
  }

  clearPlayRetries = () => {
    this.playRetryTimers.forEach(clearTimeout)
    this.playRetryTimers = []
  }

  initVideo = url => {
    if (!url) return

    warmVideoCache(url)
    this.clearPlayRetries()
    requestAnimationFrame(() => {
      this.observeVideoVisibility()
      this.startVideoPlayback()
    })
  }

  startVideoPlayback = () => {
    this.attemptVideoPlay()
    ;[50, 150, 400].forEach(delay => {
      this.playRetryTimers.push(
        setTimeout(() => this.attemptVideoPlay(), delay),
      )
    })
  }

  attemptVideoPlay = () => {
    const video = this.videoRef.current
    if (!video || !video.src) return
    if (!video.paused) return

    video.muted = true
    video.play().catch(() => {})
  }

  observeVideoVisibility = () => {
    const wrapper = this.videoWrapperRef.current
    const video = this.videoRef.current
    if (!wrapper || !video) return

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const el = this.videoRef.current
          if (!el) return
          if (entry.isIntersecting) {
            if (el.paused) el.play().catch(() => {})
          } else {
            el.pause()
          }
        })
      },
      { rootMargin: '100px 0px', threshold: 0 },
    )
    this.intersectionObserver.observe(wrapper)
    this.attemptVideoPlay()
  }

  setVideoRef = el => {
    this.videoRef.current = el
    if (el) {
      requestAnimationFrame(() => {
        this.observeVideoVisibility()
        this.startVideoPlayback()
      })
    }
  }

  handleVideoReady = () => {
    this.attemptVideoPlay()
  }

  getPosition = position => {
    this.setState({
      position: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      },
    })
  }

  lazyLoadImage = () => {
    const { desktopBackgroundUrl } = this.props

    this.imageLoader.src = desktopBackgroundUrl

    this.imageLoader.addEventListener('load', this.setLazyUrl)
  }

  setLazyUrl = e => {
    this.setState({
      lazyUrl: e.currentTarget.src,
    })
  }

  render() {
    const {
      layout,
      title,
      caption,
      handleChangeMall,
      redirectToWayfinding,
      redirectToMallDirectory,
      selectedMall,
      showMallDropdown,
      errors,
      variant,
      dropdownCaption,
      readMoreLink,
      selectedMallSlugName,
      featuredSection,
      heroBannerLeft,
      desktopBackgroundUrl,
    } = this.props

    const { lazyUrl } = this.state
    const useVideo = VIDEO_URL_PATTERN.test(desktopBackgroundUrl || '')

    const selectedMallName = selectedMall && selectedMall.name
    const background = []

    if (
      variant === 'rewards' ||
      variant === 'watch' ||
      Boolean(selectedMallName)
    ) {
      background.push(`linear-gradient(
        to bottom right,
        rgba(231, 71, 94, 0.90) 0%,
        rgba(235, 118, 50, 0.90) 100%
      )`)
    }

    if (!useVideo && lazyUrl && variant !== 'promos-events') {
      background.push(
        variant === 'explore'
          ? `no-repeat center / cover url('${lazyUrl}')`
          : `no-repeat bottom / cover url('${lazyUrl}')`,
      )
    }

    return (
      <BannerWrapper
        variant={variant}
        style={{
          background:
            background.length > 0
              ? background.join(',').trim()
              : ['main', 'promos-events'].includes(variant)
              ? '#2c2c2c'
              : undefined,
        }}
      >
        {useVideo && desktopBackgroundUrl ? (
          <BannerImageWrapper ref={this.videoWrapperRef} variant={variant}>
            <BannerVideo
              key={desktopBackgroundUrl}
              ref={this.setVideoRef}
              src={desktopBackgroundUrl}
              autoPlay
              loop
              muted
              playsInline
              controls={false}
              preload="auto"
              disablePictureInPicture
              disableRemotePlayback
              onLoadedData={this.handleVideoReady}
              onCanPlay={this.handleVideoReady}
              onCanPlayThrough={this.handleVideoReady}
              aria-hidden
            />
          </BannerImageWrapper>
        ) : (
          lazyUrl && (
            <BannerImageWrapper
              variant={variant}
              style={{
                height: ['explore', 'rewards', 'promos-events'].includes(
                  variant,
                )
                  ? '100%'
                  : '75%',
              }}
            >
              <BannerImage
                $bgUrl={lazyUrl}
                $variant={variant}
                style={{
                  height: ['rewards', 'main'].includes(variant)
                    ? '100%'
                    : '75%',
                  opacity: ['explore', 'rewards', 'watch'].includes(variant)
                    ? 0.2
                    : 1,
                  ...(['main'].includes(variant) && {
                    backgroundPosition: 'top center',
                  }),
                  ...(!['main', 'promos-events'].includes(variant) && {
                    backgroundPosition: 'center',
                  }),
                }}
              />
              {variant === 'promos-events' && (
                <>
                  <PromosBannerOverlayTop />
                  <PromosBannerOverlayRight />
                </>
              )}
            </BannerImageWrapper>
          )
        )}
        <BannerTopGradient variant={variant} />
        <BannerBottomGradient
          variant={variant}
          featuredDisplayed={Boolean(featuredSection)}
        />
        {variant === 'rewards' && <BannerRewardsImage />}
        <ContentWrapper variant={variant}>
          <ContentContainer variant={variant} heroBannerLeft={heroBannerLeft}>
            <ContentArea
              layout={layout}
              variant={variant}
              hasSelectedMall={!!selectedMallName}
            >
              <TitleContainer
                style={{
                  marginBottom: ['explore', 'watch'].includes(variant)
                    ? null
                    : '3rem',
                }}
              >
                {typeof title === 'function' ? title() : <Title>{title}</Title>}
              </TitleContainer>
              <SubtitleContainer>
                <Subtitle variant={variant}>
                  {showMallDropdown ? truncateString(caption, 150) : caption}
                  {readMoreLink && (
                    <>
                      <br />
                      <StyledLink to={readMoreLink}>
                        Read more &raquo;
                      </StyledLink>
                    </>
                  )}
                </Subtitle>
                {showMallDropdown && (
                  <SelectMallWrapper>
                    <SelectMallButton onClick={handleChangeMall}>
                      <span>{selectedMallName || dropdownCaption}</span>
                      <Icon name="caret" />
                    </SelectMallButton>
                    <ViewButtonsContainer>
                      <ViewMallMap
                        disabled={!selectedMallSlugName}
                        onClick={() =>
                          selectedMallSlugName
                            ? redirectToWayfinding(selectedMallSlugName)
                            : null
                        }
                      >
                        Mall Map
                      </ViewMallMap>
                      <ViewMallDirectory
                        disabled={!selectedMallSlugName}
                        onClick={() => {
                          if (selectedMallSlugName) {
                            redirectToMallDirectory(selectedMallSlugName)
                          }

                          setGACustomEvent('button_cta', {
                            button_name: 'Mall_Directory_button_home',
                          })
                        }}
                      >
                        Mall Directory
                      </ViewMallDirectory>
                    </ViewButtonsContainer>
                  </SelectMallWrapper>
                )}
              </SubtitleContainer>
            </ContentArea>
            {errors && <DropdownError>Error Loading Malls</DropdownError>}
          </ContentContainer>
        </ContentWrapper>
        {Boolean(featuredSection) && (
          <FeaturedSectionWrapper>
            <div
              style={{
                position: 'relative',
                marginTop: ['main', 'promos-events'].includes(variant)
                  ? '1rem'
                  : '20rem',
              }}
            >
              {featuredSection}
            </div>
          </FeaturedSectionWrapper>
        )}
      </BannerWrapper>
    )
  }
}

HeroBanner.defaultProps = {
  layout: 0,
  defaultMall: null,
  malls: null,
  handleChangeMall: null,
  exploreLink: '',
}

HeroBanner.propTypes = {
  layout: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  desktopBackgroundUrl: PropTypes.string,
  defaultMall: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  malls: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ),
  handleChangeMall: PropTypes.func,
  exploreLink: PropTypes.string,
}

export default HeroBanner
